import React, { useCallback, useMemo, useState } from 'react';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type {
  BankFootageItem,
  MusicVideoEditorResponse,
  MusicVideoSongMap,
  SerializedMusicVideoCut,
} from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../common/types';
import { useDocLimitNote } from './doc/useDocLimitNote';
import { useMusicVideoDoc } from './doc/useMusicVideoDoc';
import {
  MusicVideoEditorContext,
  type MusicVideoEditorContextValue,
  type MusicVideoEditorMode,
  type MusicVideoSelection,
  type MusicVideoTool,
  type RecutTarget,
} from './MusicVideoEditorContext';
import { MusicVideoEditorFrame } from './MusicVideoEditorFrame';
import { useMusicVideoShowcase } from './showcase/MusicVideoShowcaseContext';
import { ShowcaseEditorFrame } from './showcase/ShowcaseEditorFrame';
import { useEditorKeys } from './useEditorKeys';
import { useEditorSession } from './useEditorSession';

interface MusicVideoEditorProps {
  /** `showcase`: the public demo (edits in memory, no account route). Default `account`. */
  mode?: MusicVideoEditorMode;
  accountId: number;
  catalogItemUuid: string;
  cut: SerializedMusicVideoCut;
  initialDoc: MusicVideoDoc;
  data: MusicVideoEditorResponse;
  songMap: MusicVideoSongMap;
  readOnly: boolean;
  onLeave: () => void;
  onOpenCut: (cutUuid: string) => void;
  /** Showcase mode: the public audio of the song. */
  audioUrl?: string;
  /** Showcase mode: the bank clips of the cut. */
  seedClips?: readonly BankFootageItem[];
}

const noUpsell = () => undefined;

/**
 * The editor of one open cut (design b1): it holds the document with Undo and Redo, saves it by
 * itself, takes newer cuts of the server in, and gives everything to its parts through
 * `MusicVideoEditorContext`. `MusicVideoEditorFrame` draws the wide or the phone layout. The parent
 * gives a new `key` for another cut. In showcase mode `ShowcaseEditorFrame` draws the same layouts
 * with the showcase top bar and the sign-up card; the edits stay in memory.
 */
const MusicVideoEditorComponent: React.FC<MusicVideoEditorProps> = (props) => {
  const { mode = 'account', accountId, catalogItemUuid, cut, initialDoc, data, songMap, onLeave } = props;
  const { readOnly: isDemo, onOpenCut, audioUrl, seedClips } = props;
  const editor = useMusicVideoDoc(initialDoc);
  const { state, dispatch, change } = editor;
  useDocLimitNote(state.refused, state.refusedCount);
  const isDirectorWorking = cut.directorState === 'working';
  const readOnly = isDemo || isDirectorWorking;
  const session = useEditorSession({
    mode,
    accountId,
    catalogItemUuid,
    cut,
    data,
    songMap,
    doc: state.doc,
    revision: state.revision,
    dispatch,
    readOnly,
    onOpenCut,
    audioUrl,
    seedClips,
  });
  const { autosave, guard, cutActions, footage, transport } = session;
  const showUpsell = useMusicVideoShowcase()?.showUpsell ?? noUpsell;
  const [selection, select] = useState<MusicVideoSelection>(null);
  const [sectionIndex, setSectionIndex] = useState<number | null>(null);
  const [recutTarget, setRecutTarget] = useState<RecutTarget | null>(null);
  const [tool, setTool] = useState<MusicVideoTool>('footage');
  const openRecut = useCallback((target: RecutTarget) => setRecutTarget(target), []);
  const closeRecut = useCallback(() => setRecutTarget(null), []);

  const context = useMemo<MusicVideoEditorContextValue>(
    () => ({
      mode,
      accountId,
      catalogItemUuid,
      cutUuid: cut.uuid,
      data,
      songMap,
      doc: state.doc,
      change,
      dispatch,
      canUndo: editor.canUndo,
      canRedo: editor.canRedo,
      readOnly,
      selection,
      select,
      sectionIndex,
      setSectionIndex,
      openRecut,
      footage,
      transport,
      flushSave: autosave.flush,
      openCut: cutActions.openCut,
      tool,
      setTool,
      showUpsell,
    }),
    [
      accountId,
      autosave.flush,
      catalogItemUuid,
      change,
      cut.uuid,
      cutActions.openCut,
      data,
      dispatch,
      editor.canRedo,
      editor.canUndo,
      footage,
      mode,
      openRecut,
      readOnly,
      sectionIndex,
      selection,
      showUpsell,
      songMap,
      state.doc,
      tool,
      transport,
    ]
  );
  useEditorKeys(context);

  return (
    <MusicVideoEditorContext.Provider value={context}>
      {mode === 'showcase' ? (
        <ShowcaseEditorFrame recutTarget={recutTarget} onCloseRecut={closeRecut} />
      ) : (
        <MusicVideoEditorFrame
          cut={cut}
          autosave={autosave}
          guard={guard}
          cutActions={cutActions}
          isDirectorWorking={isDirectorWorking}
          recutTarget={recutTarget}
          onCloseRecut={closeRecut}
          onLeave={onLeave}
        />
      )}
    </MusicVideoEditorContext.Provider>
  );
};

export const MusicVideoEditor = TypedMemo(MusicVideoEditorComponent);
